import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import App from "./App.jsx";
import { AuthContextProvider } from "./context/authContext/AuthContext";
import { SubjectsContextProvider } from "./context/subjectsContext/SubjectsContext";
import { MaterialsContextProvider } from "./context/materialsContext/MaterialsContext";
import { TasksContextProvider } from "./context/tasksContext/TasksContext";
import { SchedulesContextProvider } from "./context/schedulesContext/SchedulesContext";
import { DoubtsContextProvider } from "./context/doubtsContext/DoubtsContext";

const OrientationGuard = ({ children }) => {
  const [blocked, setBlocked] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  // Refined heuristics:
  // Treat as tablet if touch + minSide >= 600 OR (devicePixelRatio * minSide) >= 1200 (large logical size)
  // Block landscape phones: touch + minSide < 600 + width > height
  const computeBlocked = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const landscape = w > h;
    const isTouch = window.matchMedia?.('(pointer:coarse)')?.matches || false;
    const minSide = Math.min(w, h);
    const maxSide = Math.max(w, h);
    const dpr = window.devicePixelRatio || 1;
    const logicalMin = minSide * dpr;
    const tabletThreshold = (minSide >= 600) || (logicalMin >= 1200); // second condition catches hi-dpi tablets
    const isTablet = isTouch && tabletThreshold;
    const isLandscapePhone = isTouch && !isTablet && landscape;
    return isTablet || isLandscapePhone;
  };

  const check = () => {
    const shouldBlock = computeBlocked();
    setBlocked(shouldBlock);
    setShowOverlay(shouldBlock); // overlay flag separate so we can animate later if desired
  };

  useEffect(() => {
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // Important: do NOT unmount children; keep app & auth context alive
  return (
    <>
      {children}
      {showOverlay && (
        <div className="orientation-block">
          <div className="orientation-inner">
            <h2>Unsupported Layout</h2>
            <p>Please switch to portrait phone or use a laptop. Tablets and landscape phone mode aren’t supported.</p>
          </div>
        </div>
      )}
    </>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <OrientationGuard>
      <AuthContextProvider>
        <SubjectsContextProvider>
          <MaterialsContextProvider>
            <TasksContextProvider>
              <SchedulesContextProvider>
                <DoubtsContextProvider>
                  <App />
                </DoubtsContextProvider>
              </SchedulesContextProvider>
            </TasksContextProvider>
          </MaterialsContextProvider>
        </SubjectsContextProvider>
      </AuthContextProvider>
    </OrientationGuard>
  </React.StrictMode>,
  document.getElementById("root")
);
